/**
 * PocketFiler - Developers / API Docs Landing Page
 * Public polished entry point to the API. Links to full Swagger docs.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Code, Zap, Book, Key, Webhook, ExternalLink, Copy, Shield, Github } from 'lucide-react';
import { useTranslation } from '../i18n';


const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.pocketfiler.com';


export default function DevelopersPage() {
  const { t } = useTranslation();

  const codeExample = `curl -X POST ${API_URL}/api/contract-templates/nda_mutual/draft \\
  -H "Authorization: Bearer $YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "field_values": {
      "party_a_name": "Acme Corp",
      "party_b_name": "Widgets LLC",
      "effective_date": "2026-04-18",
      "governing_law": "Colorado"
    }
  }'`;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>
          <div className="flex items-center gap-2">
            <Code className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">PocketFiler Developers</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 border-b bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
            <Code className="h-3 w-3" />
            REST API · v1
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-4">Build on PocketFiler</h1>
          <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Full programmatic access to contracts, documents, deals, clients, and AI features. Authenticated via API keys or OAuth.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href={`${API_URL}/docs`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 flex items-center gap-2"
            >
              <Book className="h-4 w-4" /> Full API Reference <ExternalLink className="h-3 w-3" />
            </a>
            <Link
              to="/developer-settings"
              className="px-5 py-2.5 rounded-lg border font-medium text-sm hover:bg-muted flex items-center gap-2"
            >
              <Key className="h-4 w-4" /> Get API Key
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Start */}
      <section className="py-12 border-b">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl font-bold mb-6">Quick Start</h2>
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {[
              { step: '1', title: 'Get your API key', body: 'Settings → API Keys & Webhooks. Generate a key with the permissions you need.' },
              { step: '2', title: 'Make a request', body: 'Include your key in the Authorization header. Start with GET /api/health to verify.' },
              { step: '3', title: 'Build', body: 'Every feature in PocketFiler has an API endpoint. Contracts, docs, deals, webhooks.' },
            ].map(s => (
              <div key={s.step} className="rounded-xl border bg-background p-5">
                <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary font-bold flex items-center justify-center mb-3">
                  {s.step}
                </div>
                <h3 className="font-semibold mb-1">{s.title}</h3>
                <p className="text-sm text-muted-foreground">{s.body}</p>
              </div>
            ))}
          </div>

          {/* Code sample */}
          <div className="rounded-xl border bg-gray-900 dark:bg-black overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-800">
              <span className="text-xs text-gray-400 font-mono">Generate an AI contract</span>
              <button
                onClick={() => navigator.clipboard.writeText(codeExample)}
                className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
              >
                <Copy className="h-3 w-3" /> Copy
              </button>
            </div>
            <pre className="p-4 text-xs text-gray-300 overflow-x-auto">
              <code>{codeExample}</code>
            </pre>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 border-b">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl font-bold mb-6">What you can build</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { icon: Zap, title: 'AI Contracts', body: 'Generate, customize, translate, and finalize contracts programmatically. NDA, SOW, MSA, and more.' },
              { icon: Webhook, title: 'Webhooks', body: 'Real-time events for signatures, document uploads, deal status changes, and more.' },
              { icon: Shield, title: 'Enterprise Security', body: '2FA-ready, JWT with refresh tokens, audit logs, and scoped API keys.' },
              { icon: Book, title: 'Zapier Integration', body: '3 triggers and 2 actions already published — connect PocketFiler to 5,000+ apps.' },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="rounded-xl border bg-background p-5 flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{f.title}</h3>
                    <p className="text-sm text-muted-foreground">{f.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Endpoints */}
      <section className="py-12 border-b">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl font-bold mb-6">Core Endpoints</h2>
          <div className="rounded-xl border bg-background overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="text-left py-2.5 px-4 font-semibold">Resource</th>
                  <th className="text-left py-2.5 px-4 font-semibold">Endpoint</th>
                  <th className="text-left py-2.5 px-4 font-semibold hidden md:table-cell">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y font-mono text-xs">
                {[
                  { r: 'Auth', e: 'POST /api/auth/login', d: 'Login with email + password' },
                  { r: 'Auth', e: 'POST /api/auth/refresh', d: 'Refresh access token' },
                  { r: 'Contracts', e: 'GET /api/contract-templates', d: 'List available templates' },
                  { r: 'Contracts', e: 'POST /api/contract-templates/{key}/draft', d: 'Generate a contract' },
                  { r: 'Documents', e: 'GET /api/documents', d: 'List your documents' },
                  { r: 'Documents', e: 'POST /api/documents/presigned-upload', d: 'Upload a document' },
                  { r: 'Deals', e: 'GET /api/deals', d: 'List deals' },
                  { r: 'Clients', e: 'GET /api/clients', d: 'List clients' },
                  { r: 'AI', e: 'POST /api/ai/generate', d: 'AI content generation' },
                  { r: 'AI', e: 'POST /api/ai/translate-document', d: 'Translate HTML document' },
                  { r: 'Webhooks', e: 'POST /api/developers/webhooks', d: 'Create a webhook' },
                ].map((row, i) => (
                  <tr key={i} className="hover:bg-muted/30">
                    <td className="py-2 px-4 text-foreground font-semibold">{row.r}</td>
                    <td className="py-2 px-4 text-primary">{row.e}</td>
                    <td className="py-2 px-4 text-muted-foreground hidden md:table-cell">{row.d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-3 text-center">
            Full reference with request/response schemas: <a href={`${API_URL}/docs`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Swagger UI →</a>
          </p>
        </div>
      </section>

      {/* Rate limits + support */}
      <section className="py-12">
        <div className="container mx-auto px-4 max-w-4xl grid md:grid-cols-2 gap-4">
          <div className="rounded-xl border bg-background p-5">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Zap className="h-4 w-4 text-primary" /> Rate Limits
            </h3>
            <p className="text-sm text-muted-foreground mb-2">Fair use, scoped per endpoint:</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Auth: 5 requests / minute</li>
              <li>• AI generation: 10 requests / minute</li>
              <li>• Contract drafts: 5 requests / minute</li>
              <li>• File uploads: 20 requests / minute</li>
              <li>• Translation: 5 requests / minute</li>
            </ul>
          </div>
          <div className="rounded-xl border bg-background p-5">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Book className="h-4 w-4 text-primary" /> Developer Support
            </h3>
            <p className="text-sm text-muted-foreground mb-3">Questions? We respond fast.</p>
            <a href="mailto:info@pocketfiler.com" className="text-sm text-primary hover:underline">info@pocketfiler.com</a>
            <p className="text-xs text-muted-foreground mt-2">SLA: first response within 1 business day.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
